import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { colors, fontWeights } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import basketUtils from 'utils/Basket';
import DeliveryFrequencyUtils from 'utils/DeliveryFrequency';
import { Box, Text } from 'components/ui';

const { formatSavingAmountString, formatCurrency } = DeliveryFrequencyUtils;

/**
 * Displays a formatted swatch price derived from the SKU's list price and replenishment adjuster.
 * Shows a discount amount alongside a struck-through original price when a replenishment discount applies.
 *
 * @param {object} props
 * @param {object} props.sku - SKU object containing pricing fields
 * @param {string} props.sku.listPrice - Raw list price string (e.g. "$12.00")
 * @param {string} props.sku.replenishmentAdjusterPrice - Raw replenishment discount price string; non-zero triggers discount display
 * @param {object} props.currentProduct - Current product used to determine quantity for price formatting
 * @param {number} props.currentProduct.qty - Quantity; defaults to 1 when absent
 */
const SwatchPrice = ({ sku = {}, currentProduct = {} }) => {
    const priceData = useMemo(() => {
        const quantity = Number(currentProduct.qty ?? 1);

        return {
            price: formatCurrency(parseFloat(basketUtils.removeCurrency(sku.listPrice)), quantity),
            discount: parseFloat(basketUtils.removeCurrency(sku.replenishmentAdjusterPrice)) ? formatSavingAmountString(sku, quantity) : null
        };
    }, [sku, currentProduct.qty]);

    return (
        <Box is='span' display='block' data-testid='swatch-price'>
            {priceData.discount ? (
                <>
                    <Text css={styles.discount}>{priceData.discount}</Text> <Text is='del'>{priceData.price}</Text>
                </>
            ) : (
                <Text>{priceData.price}</Text>
            )}
        </Box>
    );
};

const styles = {
    discount: {
        fontWeight: fontWeights.bold,
        color: colors.red
    }
};

SwatchPrice.propTypes = {
    sku: PropTypes.shape({
        listPrice: PropTypes.string,
        replenishmentAdjusterPrice: PropTypes.string,
        replenishmentAdjuster: PropTypes.string,
        replenishmentAdjusterType: PropTypes.string
    }),
    currentProduct: PropTypes.shape({
        qty: PropTypes.number
    })
};

export default wrapFunctionalComponent(SwatchPrice, 'SwatchPrice');
